import { GoogleGenAI, Type } from "@google/genai";
import { CreativeBrief, CampaignConcept } from "../types";

const getAI = () => {
  const apiKey = process.env.API_KEY || localStorage.getItem('gemini_api_key') || '';
  if (!apiKey) {
    console.warn("API Key missing. Please provide a key via settings.");
  }
  return new GoogleGenAI({ apiKey });
};

const synthesizePrompt = (brief: CreativeBrief): string => {
  const anatomicalParts = brief.gender === 'female' 
    ? `Body: ${brief.bustLine} bust, ${brief.waistRatio} snatched waist, ${brief.hipsSculpt} hips.`
    : `Body: ${brief.makeupPalette} grooming, ${brief.waistRatio} core, ${brief.hipsSculpt} physique.`;

  const segments = [
    `Subject: ${brief.gender} virtual influencer.`,
    `Aesthetic: ${brief.vibe}.`,
    `Location: ${brief.scene}.`,
    `Wardrobe: ${brief.outfit}.`,
    `Lighting: ${brief.lighting}.`,
    `Optics: ${brief.lens}.`,
    `Realism Profile: ${brief.realismTuning}.`,
    brief.hairStyle !== "As in photo" ? `Hair: ${brief.hairStyle}.` : "",
    `Texture: ${brief.skinStyle}, high-end editorial finish.`,
    anatomicalParts,
    `Camera Angle: ${brief.cameraAngle}.`,
    brief.customPrompt ? `Style Details: ${brief.customPrompt}.` : ""
  ].filter(Boolean);

  return segments.join(" ");
};

export const refineBriefPrompt = async (currentPrompt: string): Promise<string> => {
  const ai = getAI();
  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Transform this fashion prompt into a highly technical, "Baddie" aesthetic, cinematic editorial directive: "${currentPrompt}". Focus on fabric textures (latex, satin, fur), luxury lighting, and confident baddie poses. Keep it under 50 words.`,
    });
    return response.text?.trim() || currentPrompt;
  } catch (error) {
    console.error("Refinement failed", error);
    return currentPrompt;
  }
};

export const generateCampaignConcepts = async (brief: CreativeBrief): Promise<CampaignConcept[]> => {
  const ai = getAI();
  const synthPrompt = synthesizePrompt(brief);

  const systemInstruction = `YOU ARE A VIRTUAL INFLUENCER CREATIVE DIRECTOR SPECIALIZING IN THE "BADDIE" AESTHETIC. 
  Storyboard 1 high-fashion campaign theme for a ${brief.gender} virtual twin.
  FOCUS ON: Confident posture, luxury environments, trend-setting styling, and high-glam lighting.
  STRICT RULE: Do NOT describe the subject's facial features. The identity is provided by the anchor images.
  Output JSON ARRAY ONLY with 1 item.`;

  const parts: any[] = [{ text: `BASE DIRECTIVE: ${synthPrompt}` }];
  
  Object.values(brief.anchorImages).forEach((img) => {
    if (img) {
      const mime = img.split(';')[0].split(':')[1] || 'image/jpeg';
      parts.push({ inlineData: { mimeType: mime, data: img.split(',')[1] } });
    }
  });

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-pro-preview',
      contents: { parts },
      config: {
        systemInstruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING },
              themeIndex: { type: Type.NUMBER },
              title: { type: Type.STRING },
              description: { type: Type.STRING },
              prompt: { type: Type.STRING },
            },
            required: ["id", "themeIndex", "title", "description", "prompt"],
          },
        },
      },
    });

    return JSON.parse(response.text?.trim() || "[]");
  } catch (error: any) {
    if (error.message?.includes("Requested entity was not found") || error.message?.includes("API key not valid")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};

export const generateTwinImage = async (
  brief: CreativeBrief,
  conceptPrompt: string
): Promise<string> => {
  const ai = getAI();
  const model = 'gemini-3-pro-image-preview';
  
  const parts: any[] = [];
  const activeAnchors = Object.values(brief.anchorImages).filter(img => img !== null);
  
  activeAnchors.forEach((img) => {
    const mime = img!.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: img!.split(',')[1] } });
  });

  const anatomyRef = brief.gender === 'female' 
    ? `${brief.bustLine} bust line, ${brief.waistRatio} snatched waist, ${brief.hipsSculpt} hourglass hips`
    : `${brief.makeupPalette} grooming, ${brief.waistRatio} core, ${brief.hipsSculpt} physique`;

  const finalPrompt = `
    IDENTITY ANCHORS: The attached photos are the PERMANENT FACE REFERENCES. 
    TASK: Re-render this EXACT PERSON as a high-fashion "Baddie" persona.
    SCENE: ${conceptPrompt}. 
    MANDATORY BADDIE RULES: 
    - Face MUST be an identical match to the reference photos.
    - Style the character in a confident, high-glam pose.
    - Makeup: ${brief.makeupPalette} beat, impeccable blending.
    - Body: ${brief.bodyType}, ${anatomyRef}.
    
    PHOTOGRAPHIC TECHNICAL SPECS:
    - Profile: ${brief.realismTuning}
    - Lighting: ${brief.lighting}
    - Optics: ${brief.lens}
    - Camera Angle: ${brief.cameraAngle}
    - High-end fashion editorial quality, visible skin texture, cinematic color grading.
    
    OUTPUT: Ultra-realistic masterpiece photograph.
  `.trim();

  parts.push({ text: finalPrompt });

  try {
    const response = await ai.models.generateContent({
      model,
      contents: { parts },
      config: {
        imageConfig: {
          aspectRatio: brief.aspectRatio,
          imageSize: brief.imageSize || "1K"
        }
      },
    });

    const imagePart = response.candidates?.[0]?.content?.parts?.find(p => !!p.inlineData);
    if (imagePart?.inlineData) return `data:image/png;base64,${imagePart.inlineData.data}`;
    throw new Error("PRODUCTION_ERROR");
  } catch (error: any) {
    if (error.message?.includes("Requested entity was not found") || error.message?.includes("API key not valid")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};